import React, { createContext, useContext, useEffect, useState } from "react";
import { addCssAttribute } from "../utils/util";
import LoadingSpinner from "../component/LoadingSpinner/LoadingSpinner";
import { Helmet } from "react-helmet-async";
import apiService from "../api/apiService";
import { endPoints } from "../api/endpoints";

const BrandContext = createContext();

export const useBrand = () => useContext(BrandContext);

export const BrandProvider = ({ children }) => {
    const [brand, setBrand] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const applyConfig = (config) => {
            if (!isMounted) return;

            const rawUiConfig = (config?.ui && typeof config.ui === "object") ? config.ui : {};
            const uiConfig = {
                ...rawUiConfig,
                show_copyright: rawUiConfig.show_copyright ?? rawUiConfig.showCopyright,
                show_pricing_see_all_features: rawUiConfig.show_pricing_see_all_features ?? rawUiConfig.showPricingSeeAllFeatures,
                sso_login: rawUiConfig.sso_login ?? rawUiConfig.ssoLogin ?? config?.sso_login ?? config?.ssoLogin,
                powered_by_branding: rawUiConfig.powered_by_branding ?? rawUiConfig.poweredByBranding ?? config?.powered_by_branding ?? config?.poweredByBranding ?? true,
            };

            const finalConfig = {
                app_name: config?.app_name ?? config?.appName,
                brand_name: config?.brand_name ?? config?.brandName,
                landing_url: config?.landing_url ?? config?.landingUrl,
                logo_pc: config?.logo_pc ?? config?.logoPc,
                logo_mobile: config?.logo_mobile ?? config?.logoMobile,
                brand_logo: config?.brand_logo ?? config?.brandLogo ?? config?.powered_by_logo ?? config?.poweredByLogo,
                whatsapp_empty_state_image: config?.whatsapp_empty_state_image ?? config?.whatsappEmptyStateImage,
                route_config: (() => {
                    const raw = (config.route_config && typeof config.route_config === "object")
                        ? config.route_config
                        : ((config.routeConfig && typeof config.routeConfig === "object")
                            ? config.routeConfig
                            : undefined);
                    if (!raw) return undefined;
                    // Normalize camelCase keys used by brandConfig.json / partner API.
                    return {
                        ...raw,
                        base_home: raw.base_home ?? raw.baseHome,
                        enable_all_route: raw.enable_all_route ?? raw.enableAllRoute,
                    };
                })(),
                ui: uiConfig,
                favicon: config?.favicon,
                apple_touch_icon: config?.apple_touch_icon ?? config?.appleTouchIcon,
                primary_color: config?.primary_color ?? config?.primaryColor,
                primary_dark_color: config?.primary_dark_color ?? config?.primaryDarkColor,
            };

            setBrand(finalConfig);

            if (finalConfig.primary_color) {
                addCssAttribute("--primary", finalConfig.primary_color);
            }

            if (finalConfig.primary_dark_color) {
                addCssAttribute("--primary-dark", finalConfig.primary_dark_color);
            }
            


            if (finalConfig.favicon) {
                let link = document.getElementById("favicon");
                if (link) {
                    link.href = finalConfig.favicon;
                }
            }

            if (finalConfig.apple_touch_icon) {
                let appleLink = document.getElementById("apple-touch-icon");
                if (appleLink) {
                    appleLink.href = finalConfig.apple_touch_icon;
                }
            }

            setIsLoading(false);
        };

        const fetchPartnerConfigWithRetry = async (retries) => {
            try {
                const response = await apiService.get(endPoints.partner.config);
                return response?.data?.data ?? response?.data;
            } catch (error) {
                if (retries > 1) {
                    await new Promise((r) => setTimeout(r, 1000));
                    return fetchPartnerConfigWithRetry(retries - 1);
                }
                throw error;
            }
        };

        const fetchBrandConfig = async () => {
            try {
                const responseData = await fetchPartnerConfigWithRetry(3);
                const config = (responseData && typeof responseData === "object" && responseData.data)
                    ? responseData.data
                    : responseData;

                applyConfig(config || {});
            } catch (error) {
                console.error("Failed to load brand configuration.", error);
                applyConfig({});
            }
        };

        fetchBrandConfig();

        return () => {
            isMounted = false;
        };
    }, []);

    if (isLoading) {
        return <LoadingSpinner fill="full-screen" isLoading={true} text="Initializing workspace..." />;
    }

    const appName = brand?.app_name || brand?.brand_name || "";
    const description = appName
        ? `${appName}: Simple, Affordable CRM, streamlining lead management, appointment booking, and client onboarding. Create custom forms with e-signature capabilities, distribute leads efficiently, and integrate with WhatsApp for seamless communication.`
        : "";
    const title = appName ? `${appName} - Simple, Affordable CRM` : "";

    return (
        <BrandContext.Provider value={brand}>
            <Helmet>
                <title>{appName}</title>
                <meta name="keywords" content={appName ? `${appName}, productivity tool, lead management, appointment booking, client onboarding, custom forms, e-signature, WhatsApp integration` : ""} />
                <meta name="description" content={description} />
                <meta name="author" content={appName} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={brand?.logo_pc || ""} />
                <meta property="og:url" content={brand?.landing_url || ""} />
                <meta property="og:site_name" content={appName} />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={brand?.logo_pc || ""} />
                <link rel="icon" type="image/png" sizes="32x32" href={brand?.favicon || ""} />
                <link rel="apple-touch-icon" sizes="180x180" href={brand?.apple_touch_icon || ""} />
            </Helmet>
            {children}
        </BrandContext.Provider>
    );
};
